
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateActivity, CreateActivityData } from "@/hooks/use-activities";
import { useContacts } from "@/hooks/use-contacts";
import { useStaff } from "@/hooks/use-staff";
import { usePartners } from "@/hooks/use-partners";
import { Plus, Calendar } from "lucide-react";

const activitySchema = z.object({
  activity_type: z.enum(["Meeting", "Phone Call", "Email Communication", "Proposal Presentation", "Site Visit", "Follow-up", "Other"]),
  activity_date: z.string().min(1, "Activity date is required"),
  title: z.string().min(1, "Title is required"),
  notes: z.string().optional(),
  contact_id: z.string().optional(),
  performed_by_staff_id: z.string().optional(),
  performed_by_partner_id: z.string().optional(),
}).refine((data) => {
  return data.performed_by_staff_id || data.performed_by_partner_id;
}, {
  message: "Must select either a staff member or partner",
  path: ["performed_by_staff_id"]
});

interface ActivityModalProps {
  defaultContactId?: string;
  defaultStaffId?: string;
  defaultPartnerId?: string;
}

export const ActivityModal = ({ defaultContactId, defaultStaffId, defaultPartnerId }: ActivityModalProps) => {
  const [open, setOpen] = useState(false);
  const createActivity = useCreateActivity();
  const { data: contacts } = useContacts();
  const { data: staff } = useStaff();
  const { data: partners } = usePartners();

  const form = useForm<CreateActivityData>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      activity_type: "Meeting",
      activity_date: new Date().toISOString().slice(0, 13) + ":00",
      title: "",
      notes: "",
      contact_id: defaultContactId || "",
      performed_by_staff_id: defaultStaffId || "",
      performed_by_partner_id: defaultPartnerId || "",
    },
  });

  const onSubmit = async (data: CreateActivityData) => {
    const cleanedData = {
      ...data,
      contact_id: data.contact_id === "none" || !data.contact_id ? undefined : data.contact_id,
      performed_by_staff_id: data.performed_by_staff_id === "none" || !data.performed_by_staff_id ? undefined : data.performed_by_staff_id,
      performed_by_partner_id: data.performed_by_partner_id === "none" || !data.performed_by_partner_id ? undefined : data.performed_by_partner_id,
      notes: data.notes || undefined,
    };

    createActivity.mutate(cleanedData, {
      onSuccess: () => {
        setOpen(false);
        form.reset({
          activity_type: "Meeting",
          activity_date: new Date().toISOString().slice(0, 13) + ":00",
          title: "",
          notes: "",
          contact_id: defaultContactId || "",
          performed_by_staff_id: defaultStaffId || "",
          performed_by_partner_id: defaultPartnerId || "",
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90">
          <Plus className="h-4 w-4 mr-2" />
          Add Activity
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm sm:max-w-lg px-4 sm:px-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Add New Activity
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="activity_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Activity Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Phone Call">Phone Call</SelectItem>
                      <SelectItem value="Email Communication">Email Communication</SelectItem>
                      <SelectItem value="Proposal Presentation">Proposal Presentation</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                      <SelectItem value="Follow-up">Follow-up</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., Initial consultation meeting" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="activity_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Activity Date & Time *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" step="3600" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="contact_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a contact" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No contact selected</SelectItem>
                      {contacts?.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.first_name} {contact.last_name} - {contact.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="performed_by_staff_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Staff Member</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value === "none" ? undefined : value);
                        if (value && value !== "none") form.setValue("performed_by_partner_id", "");
                      }} 
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select staff" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No staff selected</SelectItem>
                        {staff?.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.first_name} {member.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="performed_by_partner_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Partner</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value === "none" ? undefined : value);
                        if (value && value !== "none") form.setValue("performed_by_staff_id", "");
                      }} 
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select partner" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No partner selected</SelectItem>
                        {partners?.map((partner) => (
                          <SelectItem key={partner.id} value={partner.id}>
                            {partner.first_name} {partner.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Activity details, outcomes, follow-up actions..." 
                      className="min-h-[100px]" 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createActivity.isPending} className="bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90">
                {createActivity.isPending ? "Creating..." : "Create Activity"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
